// web/src/components/RoastToastToggle.tsx
// Figma create-screen re-skin (node 2001:2 "roast" / 2007:838 "toast"): binary
// roast/toast mode toggle — replaces RoastToastSlider's 5-stop intensity
// slider. A mode is always selected (no neutral); profanity is fixed to
// "savage" for both modes now, so this control only ever needs to pick a side.
//
// Named RoastToastToggle (not ModeToggle) to avoid colliding with the
// existing, unrelated `ModeToggle.tsx` used by StudioCreate.tsx (aria-pressed
// Roast/Toast buttons, different colors/labels) — that component is untouched.
import { useRef } from "react";
import type { JSX } from "react";
import { motion, useAnimationControls } from "motion/react";
import type { Mode } from "@/types/roast";
import { cn } from "@/lib/utils";
import { EXTREME_HOLD_MS, EXTREME_EMOJI, EXTREME_ACCENT } from "@/config/extremeMode";

export interface RoastToastToggleProps {
  mode: Mode;
  onChange: (mode: Mode) => void;
  extreme?: boolean;
  onToggleExtreme?: () => void;
  className?: string;
}

/** Selected-segment fill per mode — matches the Figma CTA/toggle accent exactly. */
const ACCENT: Record<Mode, string> = {
  roast: "#e62c36",
  toast: "#2c71e6",
};

const SEGMENTS: { mode: Mode; emoji: string; label: string }[] = [
  { mode: "toast", emoji: "💖", label: "toast" },
  { mode: "roast", emoji: "💀", label: "roast" },
];

/** Dark frosted pill containing the two segments; the selected segment gets a
 *  filled, mode-colored pill (per the Figma "roast"/"toast" frames). */
export function RoastToastToggle({
  mode,
  onChange,
  extreme = false,
  onToggleExtreme,
  className,
}: RoastToastToggleProps): JSX.Element {
  const controls = useAnimationControls();
  // Spring "poke" the whole toggle when a segment is clicked.
  const poke = () => {
    controls.set({ scale: 0.95 });
    void controls.start({ scale: 1 }, { type: "spring", stiffness: 460, damping: 12 });
  };
  // Long-press unlock: a press held past EXTREME_HOLD_MS toggles extreme mode instead of
  // switching side. A quick tap (click, or a sub-threshold pointer press) still switches.
  const holdTimer = useRef<number | null>(null);
  const heldRef = useRef(false);
  const clearHold = () => {
    if (holdTimer.current !== null) { clearTimeout(holdTimer.current); holdTimer.current = null; }
  };
  const startHold = () => {
    heldRef.current = false;
    holdTimer.current = window.setTimeout(() => {
      heldRef.current = true;      // suppress the click that follows this press
      onToggleExtreme?.();
    }, EXTREME_HOLD_MS);
  };
  return (
    <motion.div
      role="radiogroup"
      aria-label="Roast or toast mode"
      animate={controls}
      className={cn(
        // select-none + the -webkit- variants: press-and-hold (the extreme unlock) must
        // not select the segment text or pop iOS's callout/selection menu.
        "inline-flex select-none items-center gap-1 rounded-full bg-black/70 p-1.5 backdrop-blur-md [-webkit-touch-callout:none] [-webkit-user-select:none]",
        className,
      )}
    >
      {SEGMENTS.map(({ mode: segMode, emoji, label }) => {
        const selected = mode === segMode;
        return (
          <button
            key={segMode}
            type="button"
            role="radio"
            aria-checked={selected}
            onPointerDown={startHold}
            onPointerUp={clearHold}
            onPointerLeave={clearHold}
            onPointerCancel={clearHold}
            onClick={() => {
              if (heldRef.current) { heldRef.current = false; return; } // was a hold → don't switch
              poke();
              onChange(segMode);
            }}
            className={cn(
              "relative flex select-none items-center rounded-full px-5 py-2.5 text-base font-bold transition-colors [-webkit-touch-callout:none] [-webkit-user-select:none] sm:px-6 sm:py-3 sm:text-lg",
              selected ? "text-white" : "text-white/70 hover:text-white/90",
            )}
          >
            {selected && (
              /* Shared-layout pill: slides (with a spring) to the active segment
                 on toggle instead of popping. */
              <motion.span
                aria-hidden="true"
                data-testid="rt-active-pill"
                layoutId="rt-active-pill"
                className="absolute inset-0 z-0 rounded-full shadow-[0_6px_10px_rgba(0,0,0,0.25)]"
                style={{ backgroundColor: extreme ? EXTREME_ACCENT[segMode] : ACCENT[segMode] }}
                transition={{ type: "spring", stiffness: 480, damping: 30 }}
              />
            )}
            <span className="relative z-10 flex items-center gap-2">
              <span aria-hidden="true" className={selected ? "" : "opacity-80"}>
                {extreme ? EXTREME_EMOJI[segMode] : emoji}
              </span>
              {label}
            </span>
          </button>
        );
      })}
    </motion.div>
  );
}
